import {
  ExclamationTriangleIcon,
} from "@heroicons/react/24/outline";
import { City, State } from "../../../Models";
import { Dialog, Transition } from "@headlessui/react";
import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import Loading from "../../Shared/Loading";
import { useNotification } from "../../../context/NotificationContext";
import { isQuarantineCity } from "../../../Helpers";
import { QuarantineDataType } from "../Quarantine";

interface AlternateNameMapping {
  sourceCityId: string;
  sourceCityName: string;
  targetCityId: string;
}

export default function AlternateNameConfirmationPopup({
  isOpen,
  onCancel,
  onConfirm,
  selectedRows,
  cities,
  states,
  updateSelectedRows,
  gptCityDetails,
}: {
  isOpen: boolean;
  onCancel: () => void;
  onConfirm: (mappings: AlternateNameMapping[]) => Promise<void>;
  selectedRows: QuarantineDataType[];
  cities: City[];
  states: State[];
  updateSelectedRows: Function;
  gptCityDetails: any;
}) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState<boolean>(false);
  const cancelButtonRef = useRef(null);
  const [replaceCommon, setReplaceCommon] = useState<boolean[]>([]);
  const { success, error: notifyError } = useNotification();

  // For each selected city (City1), store the chosen target city (City2)
  const [selectedTargets, setSelectedTargets] = useState<(string | null)[]>(
    []
  );

  // Search state for city dropdown filtering
  const [searchValues, setSearchValues] = useState<string[]>([]);

  // Single active dropdown: only one open at a time, rendered via portal
  const [activeDropdownIndex, setActiveDropdownIndex] = useState<number>(-1);
  const [dropdownPosition, setDropdownPosition] = useState<{
    top: number;
    left: number;
    width: number;
    openUpward: boolean;
  } | null>(null);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const dropdownRef = useRef<HTMLUListElement | null>(null);

  // =========================================================================
  // EFFECTS
  // =========================================================================

  // Initialize states based on selectedRows length
  useEffect(() => {
    setReplaceCommon(new Array(selectedRows.length).fill(true));
    setSelectedTargets(new Array(selectedRows.length).fill(null));
    setSearchValues(new Array(selectedRows.length).fill(""));
    inputRefs.current = new Array(selectedRows.length).fill(null);
  }, [selectedRows]);

  // Sync open state with isOpen prop
  useEffect(() => {
    setOpen(isOpen);
    if (!isOpen) {
      setActiveDropdownIndex(-1);
    }
  }, [isOpen]);

  // Auto-fill from GPT / backend details if available (single-row case)
  useEffect(() => {
    if (selectedRows.length !== 1) return;

    const row = selectedRows[0];
    const cityDetails =
      (gptCityDetails?.[row.id] && gptCityDetails[row.id].corrected_name
        ? gptCityDetails[row.id]
        : (row as any).actiongpt) || null;

    if (!cityDetails?.corrected_name) return;

    const correctedName = cityDetails.corrected_name as string;
    const matchingCity = cities.find(
      (c) => c.name.toLowerCase() === correctedName.toLowerCase()
    );
    if (matchingCity) {
      setSelectedTargets([matchingCity.id]);
      setSearchValues([matchingCity.name]);
    }
  }, [gptCityDetails, selectedRows, cities]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (activeDropdownIndex === -1) return;
      const inputEl = inputRefs.current[activeDropdownIndex];
      const dropdownEl = dropdownRef.current;
      const target = e.target as Node;

      if (
        inputEl &&
        !inputEl.contains(target) &&
        (!dropdownEl || !dropdownEl.contains(target))
      ) {
        setActiveDropdownIndex(-1);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [activeDropdownIndex]);

  // Recalculate dropdown position on scroll inside the list
  useEffect(() => {
    if (activeDropdownIndex === -1) return;

    const handleScrollOrResize = () => {
      calculateDropdownPosition(activeDropdownIndex);
    };

    window.addEventListener("resize", handleScrollOrResize);
    // Also listen to scroll on any ancestor
    const scrollableParent = inputRefs.current[activeDropdownIndex]?.closest(
      ".overflow-y-auto"
    );
    scrollableParent?.addEventListener("scroll", handleScrollOrResize);

    return () => {
      window.removeEventListener("resize", handleScrollOrResize);
      scrollableParent?.removeEventListener("scroll", handleScrollOrResize);
    };
  }, [activeDropdownIndex]);

  // =========================================================================
  // DROPDOWN POSITIONING
  // =========================================================================

  const calculateDropdownPosition = (index: number) => {
    const inputEl = inputRefs.current[index];
    if (!inputEl) return;

    const rect = inputEl.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const openUpward = spaceBelow < 200;

    setDropdownPosition({
      top: openUpward ? rect.top : rect.bottom + 2,
      left: rect.left,
      width: rect.width,
      openUpward,
    });
  };

  const getFilteredCities = (index: number) => {
    const search = searchValues[index]?.toLowerCase() || "";
    if (!search) return [];
    const sourceId = selectedRows[index]?.id;
    return cities
      .filter(
        (c) => c.id !== sourceId && c.name.toLowerCase().includes(search)
      )
      .slice(0, 50);
  };

  // =========================================================================
  // HANDLERS
  // =========================================================================

  const handleCancel = () => {
    setActiveDropdownIndex(-1);
    onCancel();
    setOpen(false);
  };

  const handleConfirm = async () => {
    const mappings: AlternateNameMapping[] = selectedRows
      .map((row, idx) => {
        if (!isQuarantineCity(row)) return null;
        const targetCityId = selectedTargets[idx];
        if (!targetCityId) return null;
        return {
          sourceCityId: row.id,
          sourceCityName: row.name,
          targetCityId,
        };
      })
      .filter(Boolean) as AlternateNameMapping[];

    if (mappings.length === 0) {
      notifyError(
        "Selection Required",
        "Please select an existing city for each quarantine city."
      );
      return;
    }

    setLoading(true);
    try {
      await onConfirm(mappings);
      setOpen(false);
    } finally {
      setLoading(false);
    }
  };

  const handleReplaceCheckbox = (
    event: React.ChangeEvent<HTMLInputElement>,
    index: number
  ) => {
    setReplaceCommon((prevState) => {
      const newState = [...prevState];
      newState[index] = event.target.checked;
      return newState;
    });
  };

  const handleTargetSearch = (index: number, value: string) => {
    setSearchValues((prev) => {
      const next = [...prev];
      next[index] = value;
      return next;
    });
    setActiveDropdownIndex(index);
    calculateDropdownPosition(index);
    // Clear selection if user is typing
    setSelectedTargets((prev) => {
      const next = [...prev];
      next[index] = null;
      return next;
    });
  };

  const handleTargetSelect = (index: number, city: City) => {
    setSelectedTargets((prev) => {
      const next = [...prev];
      next[index] = city.id;
      return next;
    });
    setSearchValues((prev) => {
      const next = [...prev];
      next[index] = city.name;
      return next;
    });
    setActiveDropdownIndex(-1);
  };

  const handleInputFocus = (index: number) => {
    setActiveDropdownIndex(index);
    calculateDropdownPosition(index);
  };

  // No local editing; rows are transient and only used for mapping

  const getDisplayName = useCallback(
    (index: number) => {
      const row = selectedRows[index];
      const cityDetails =
        (gptCityDetails?.[row.id] && gptCityDetails[row.id].corrected_name
          ? gptCityDetails[row.id]
          : (row as any).actiongpt) || null;
      return cityDetails?.corrected_name || row.name;
    },
    [gptCityDetails, selectedRows]
  );

  // =========================================================================
  // PORTAL DROPDOWN RENDERER
  // =========================================================================

  const renderDropdown = () => {
    if (activeDropdownIndex === -1 || !dropdownPosition) return null;

    const filtered = getFilteredCities(activeDropdownIndex);
    if (filtered.length === 0) return null;

    const style: React.CSSProperties = {
      position: "fixed",
      left: dropdownPosition.left,
      width: dropdownPosition.width,
      zIndex: 99999,
      maxHeight: "10rem",
      overflowY: "auto",
    };

    if (dropdownPosition.openUpward) {
      style.bottom =
        window.innerHeight - dropdownPosition.top + 2;
      delete style.top;
    } else {
      style.top = dropdownPosition.top;
    }

    return createPortal(
      <ul
        ref={dropdownRef}
        className="bg-white dark:bg-darkbg border rounded shadow-lg"
        style={style}
      >
        {filtered.map((city) => (
          <li
            key={city.id}
            className="px-3 py-1 text-sm cursor-pointer hover:bg-blue-100 dark:hover:bg-gray-700"
            onMouseDown={(e) => {
              // Use mousedown to fire before blur
              e.preventDefault();
              handleTargetSelect(activeDropdownIndex, city);
            }}
          >
            {city.name} {city.state ? `(${city.state.name})` : ""}
          </li>
        ))}
      </ul>,
      document.body
    );
  };

  // =========================================================================
  // RENDER
  // =========================================================================

  return (
    <>
      <Transition.Root show={open} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-50"
          initialFocus={cancelButtonRef}
          onClose={handleCancel}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity backdrop-blur-sm" />
          </Transition.Child>

          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative transform rounded-lg bg-white dark:bg-darkbg dark:text-white shadow-xl transition-all w-full max-w-5xl mx-auto">
                  <div className="p-6">
                    <div className="flex items-center mb-4">
                      <ExclamationTriangleIcon
                        className="h-6 w-6 text-red-600"
                        aria-hidden="true"
                      />
                      <div className="text-lg ml-2 font-semibold">
                        Are you sure you want to confirm the selected cities as
                        Alternate names of existing cities?
                      </div>
                    </div>


                    <p className="mb-2 text-sm text-gray-500 dark:text-gray-400">
                      Total Cities: {selectedRows.length}
                    </p>

                    <div className="mt-2">
                      <ul className="overflow-y-auto max-h-60 pr-4 rounded-md bg-gray-100 dark:bg-darkbglight dark:text-gray-400">
                        {selectedRows.map((column, index) => (
                          <div
                            key={column.id}
                            className="flex flex-col border-b border-gray-200 last:border-b-0"
                          >
                            <div className="flex flex-row items-center justify-between">
                                <li className="pl-4 py-1 font-semibold flex flex-row items-center gap-2">
                                  <span>
                                    {`${index + 1}. ${getDisplayName(index)}`}
                                  </span>
                                  <span>{column.state.name}</span>
                                </li>
                              {isQuarantineCity(column) &&
                                (column as any).replacedCities &&
                                (column as any).replacedCities?.length > 0 && (
                                  <li className="pl-4 py-1 font-semibold">
                                    <label htmlFor={`replaceCity-alt-${index}`}>
                                      Also update{" "}
                                      {(column as any).replacedCities?.length}{" "}
                                      common cities
                                    </label>
                                    <input
                                      type="checkbox"
                                      id={`replaceCity-alt-${index}`}
                                      className="ml-2 rounded z-10 text-indigo-600"
                                      checked={replaceCommon[index]}
                                      onChange={(e) =>
                                        handleReplaceCheckbox(e, index)
                                      }
                                    />
                                  </li>
                                )}
                            </div>

                            {/* City2 selector row */}
                            <div className="pl-8 pb-2 flex items-center gap-2">
                                <label className="text-xs text-gray-600 dark:text-gray-400 whitespace-nowrap">
                                  Map to existing city:
                                </label>
                                <div className="w-72">
                                  <input
                                    ref={(el) => {
                                      inputRefs.current[index] = el;
                                    }}
                                    type="text"
                                    className="border rounded px-2 py-1 text-sm w-full"
                                    placeholder="Search existing city..."
                                    value={searchValues[index] || ""}
                                    onChange={(e) =>
                                      handleTargetSearch(
                                        index,
                                        e.target.value
                                      )
                                    }
                                    onFocus={() => handleInputFocus(index)}
                                  />
                                  {/* Dropdown is rendered via portal below */}
                                </div>
                                {selectedTargets[index] && (
                                  <span className="text-green-600 text-xs font-semibold">
                                    ✔ Selected
                                  </span>
                                )}
                              </div>
                          </div>
                        ))}
                      </ul>
                    </div>

                    <div className="flex justify-end mt-4">
                      <button
                        type="button"
                        className="bg-gray-200 text-gray-700 px-4 py-2 mr-2 rounded hover:bg-gray-400"
                        onClick={handleCancel}
                        ref={cancelButtonRef}
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 disabled:opacity-50"
                        onClick={handleConfirm}
                        disabled={loading}
                      >
                        Confirm as Alternate Name
                      </button>
                    </div>
                  </div>

                  {/* Loading */}
                  {loading && <Loading />}
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>

      {/* Dropdown rendered via portal to document.body — never clipped */}
      {renderDropdown()}
    </>
  );
}